import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Grid, Card, CardContent, CardHeader,
  Button, IconButton, Collapse, FormControl, InputLabel, Select, MenuItem,
  TextField, Chip, Avatar, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Alert, Snackbar, LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  FilterList, CalendarToday, FileDownload, PictureAsPdf, Refresh,
  School, Wc, BarChart as BarChartIcon, DonutLarge, Timeline, TableChart,
  ExpandLess, ExpandMore, Male, Female, ChildCare, Science,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, Filler,
} from 'chart.js';
import { Pie, Doughnut, Bar, Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from '../services/api';

ChartJS.register(
  ArcElement, ChartTooltip, Legend, CategoryScale,
  LinearScale, BarElement, Title, PointElement, LineElement, Filler
);

// ── Colors ───────────────────────────────────────────────────────────────────
const C = {
  primary:   '#2e7d32',
  secondary: '#43a047',
  accent:    '#ff6f00',
  gold:      '#ffd700',
  dark:      '#1b5e20',
  white:     '#ffffff',
  teal:      '#00695c',
  purple:    '#6a1b9a',
};

const CLASS_COLORS = {
  'Pre-school': C.accent,
  'STD I':      C.primary,
  'STD II':     C.secondary,
  'STD III':    C.teal,
  'STD IV':     C.purple,
  'STD V':      '#ff8f00',
  'STD VI':     '#c62828',
  'STD VII':    '#4527a0',
};

// ── Styled ───────────────────────────────────────────────────────────────────
const StyledCard = styled(Card)(({ color }) => ({
  background: `linear-gradient(135deg, ${color}25, ${color}08)`,
  borderRadius: 20, border: `1px solid ${color}40`, transition: 'all 0.3s ease',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: `0 10px 30px ${color}35` },
}));

const GlassCard = styled(Card)(() => ({
  background: C.white, borderRadius: 24,
  border: `1px solid ${C.primary}15`, boxShadow: `0 8px 32px ${C.primary}15`,
}));

const SchoolButton = styled(Button)(() => ({
  borderRadius: 8, padding: '10px 24px', textTransform: 'none', fontWeight: 600,
}));

const chartOptions = {
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 20 } },
    tooltip: { backgroundColor: C.dark, padding: 12, cornerRadius: 8 },
  },
  animation: { duration: 1500, easing: 'easeInOutQuart' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Safely parse a date string from the DB.
 * Returns a Date object or null if invalid / missing.
 */
function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Build a Date for the START of a calendar day in LOCAL time.
 * e.g. '2020-01-01' → 2020-01-01T00:00:00 local
 */
function startOfDay(dateStr) {
  return new Date(dateStr + 'T00:00:00');
}

/**
 * Build a Date for the END of a calendar day in LOCAL time.
 */
function endOfDay(dateStr) {
  return new Date(dateStr + 'T23:59:59');
}

/**
 * Normalise the API response into a plain array of students.
 * Handles: raw array, { data: [] }, { students: [] }, single object.
 */
function normaliseStudentsResponse(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data))     return raw.data;
  if (Array.isArray(raw.students)) return raw.students;
  if (Array.isArray(raw.results))  return raw.results;
  // Single object accidentally returned
  if (typeof raw === 'object') {
    console.warn('[PrimaryReports] API returned a single object, not an array:', raw);
    return [raw];
  }
  return [];
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PrimaryReports() {
  const [students,       setStudents]       = useState([]);
  const [stats,          setStats]          = useState({
    total: 0,
    byGender:      { male: 0, female: 0 },
    byStudentType: { day: 0, boarding: 0 },
    byClass:       [],
    monthlyTrend:  [],
  });
  const [loading,        setLoading]        = useState(true);
  const [selectedClass,  setSelectedClass]  = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: '2015-01-01',           // wide enough to catch any record
    end:   new Date().toISOString().split('T')[0],
  });
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [expanded,     setExpanded]     = useState({});
  const [snackbar,     setSnackbar]     = useState({ open: false, message: '', severity: 'success' });

  const toast = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch data ─────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    try {
      const studentsRes = await api.get('/primary/students/all');
      console.log('[PrimaryReports] Raw API response:', studentsRes.data);

      const studentsData = normaliseStudentsResponse(studentsRes.data);
      console.log('[PrimaryReports] Normalised students:', studentsData.length, studentsData);

      setStudents(studentsData);
      calculateLocalStats(studentsData);

    } catch (error) {
      console.error('[PrimaryReports] Failed to fetch /primary/students/all:', error);
      toast('Failed to load student data: ' + (error.response?.data?.error || error.message), 'error');

      // Fallback: fetch class-by-class
      try {
        const classes = ['Pre-school','STD I','STD II','STD III','STD IV','STD V','STD VI','STD VII'];
        const allStudents = [];
        for (const cls of classes) {
          try {
            const res = await api.get('/primary/students', { params: { class: cls } });
            const chunk = normaliseStudentsResponse(res.data);
            allStudents.push(...chunk);
          } catch (e) {
            console.warn('[PrimaryReports] Fallback fetch failed for class:', cls, e.message);
          }
        }
        console.log('[PrimaryReports] Fallback collected:', allStudents.length, 'students');
        setStudents(allStudents);
        calculateLocalStats(allStudents);
      } catch (e) {
        console.error('[PrimaryReports] Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Calculate stats locally ────────────────────────────────────────────────
  const calculateLocalStats = (studentsData) => {
    const computed = {
      total: studentsData.length,
      byGender:      { male: 0, female: 0 },
      byStudentType: { day: 0, boarding: 0 },
      byClass:       {},
      monthlyTrend:  {},
    };

    studentsData.forEach(student => {
      // Gender — case-insensitive
      const gender = (student.gender || '').toLowerCase();
      if (gender === 'male')   computed.byGender.male++;
      else if (gender === 'female') computed.byGender.female++;

      // Student type — case-insensitive
      const type = (student.student_type || 'day').toLowerCase();
      if (type === 'boarding') computed.byStudentType.boarding++;
      else                     computed.byStudentType.day++;

      // Class — trim whitespace
      const className = (student.current_class || '').trim();
      if (className) {
        computed.byClass[className] = (computed.byClass[className] || 0) + 1;
      }

      // Monthly trend
      const d = parseDate(student.created_at);
      if (d) {
        const monthYear = d.toLocaleString('default', { month: 'short' }) + ' ' + d.getFullYear();
        computed.monthlyTrend[monthYear] = (computed.monthlyTrend[monthYear] || 0) + 1;
      }
    });

    const byClassArray = Object.entries(computed.byClass).map(([classname, count]) => ({
      class: classname,
      count,
    }));

    const monthlyTrendArray = Object.entries(computed.monthlyTrend).map(([month, count]) => ({
      month,
      count,
    }));

    setStats({
      total:         computed.total,
      byGender:      computed.byGender,
      byStudentType: computed.byStudentType,
      byClass:       byClassArray,
      monthlyTrend:  monthlyTrendArray,
    });
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter ─────────────────────────────────────────────────────────────────
  const filtered = students.filter(s => {
    // Class match — trim to avoid whitespace issues
    const classMatch = selectedClass === 'all' ||
      (s.current_class || '').trim() === selectedClass;

    // Gender match — case-insensitive
    const genderMatch = selectedGender === 'all' ||
      (s.gender || '').toLowerCase() === selectedGender;

    // Date match — skip entirely if created_at is missing or invalid
    const d = parseDate(s.created_at);
    if (!d) return classMatch && genderMatch;   // no date → always include

    const start = startOfDay(dateRange.start);
    const end   = endOfDay(dateRange.end);
    const dateMatch = d >= start && d <= end;

    return classMatch && genderMatch && dateMatch;
  });

  // ── Chart data ─────────────────────────────────────────────────────────────
  const genderChart = {
    labels: ['Male', 'Female'],
    datasets: [{
      data: [stats.byGender.male, stats.byGender.female],
      backgroundColor: [`${C.secondary}cc`, `${C.accent}cc`],
      borderColor: [C.secondary, C.accent],
      borderWidth: 2,
    }],
  };

  const typeChart = {
    labels: ['Day Scholars', 'Boarding'],
    datasets: [{
      data: [stats.byStudentType.day, stats.byStudentType.boarding],
      backgroundColor: [`${C.teal}cc`, `${C.purple}cc`],
      borderWidth: 2,
    }],
  };

  const classChart = {
    labels: stats.byClass.map(c => c.class),
    datasets: [{
      label: 'Students by Class',
      data:  stats.byClass.map(c => c.count),
      backgroundColor: stats.byClass.map((_, i) => {
        const cols = [C.primary, C.secondary, C.accent, C.teal, C.purple, '#ff8f00', '#c62828', '#4527a0'];
        return `${cols[i % cols.length]}cc`;
      }),
      borderRadius: 8,
    }],
  };

  const trendChart = {
    labels: stats.monthlyTrend.map(t => t.month),
    datasets: [{
      label: 'New Enrollments',
      data:  stats.monthlyTrend.map(t => t.count),
      fill: true,
      backgroundColor: `${C.primary}18`,
      borderColor: C.primary,
      tension: 0.4,
      pointBackgroundColor: C.accent,
      pointBorderColor: C.white,
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8,
    }],
  };

  // ── Export Excel ────────────────────────────────────────────────────────────
  const handleExportExcel = () => {
    try {
      const rows = filtered.map(s => ({
        'Registration No': s.registration_number,
        'Full Name':       s.full_name,
        'Gender':          s.gender,
        'Class':           s.current_class,
        'Student Type':    s.student_type,
        'Date of Birth':   parseDate(s.date_of_birth)?.toLocaleDateString() ?? '',
        'Father Name':     s.father_name,
        'Father Phone':    s.father_phone,
        'Mother Name':     s.mother_name,
        'Mother Phone':    s.mother_phone,
        'Previous School': s.previous_school,
        'Religion':        s.religion,
        'Registered':      parseDate(s.created_at)?.toLocaleDateString() ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Primary Students');
      saveAs(
        new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `kaozya_primary_students_${new Date().toISOString().split('T')[0]}.xlsx`
      );
      toast('Excel exported successfully');
    } catch (e) {
      console.error(e);
      toast('Failed to export Excel', 'error');
    }
  };

  // ── Export PDF ──────────────────────────────────────────────────────────────
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(18); doc.setTextColor(46, 125, 50);
      doc.text('Kaozya English Medium School', 105, 15, { align: 'center' });
      doc.setFontSize(14); doc.setTextColor(255, 111, 0);
      doc.text('Primary Students Report', 105, 23, { align: 'center' });
      doc.setFontSize(10); doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 30, { align: 'center' });
      doc.text(`Total Students: ${students.length}  |  Filtered: ${filtered.length}`, 105, 37, { align: 'center' });

      doc.autoTable({
        startY: 45,
        head: [['#', 'Reg No.', 'Name', 'Gender', 'Class', 'Type']],
        body: filtered.slice(0, 50).map((s, idx) => [
          idx + 1,
          s.registration_number,
          s.full_name,
          s.gender,
          s.current_class,
          s.student_type || 'Day',
        ]),
        theme: 'striped',
        headStyles: { fillColor: [46, 125, 50] },
      });

      doc.save(`kaozya_primary_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast('PDF exported successfully');
    } catch (e) {
      console.error(e);
      toast('Failed to export PDF', 'error');
    }
  };

  const resetFilters = () => {
    setSelectedClass('all');
    setSelectedGender('all');
    setDateRange({
      start: '2015-01-01',
      end:   new Date().toISOString().split('T')[0],
    });
    setPage(0);
  };

  const toggleExpand = (key) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Header */}
      <Box sx={{
        mb: 4, p: 3, borderRadius: 3,
        background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
        boxShadow: `0 10px 30px ${C.primary}40`,
      }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: C.white, mb: 0.5 }}>
          Primary School Reports &amp; Analytics
        </Typography>
        <Typography sx={{ color: C.gold }}>
          Kaozya English Medium School — Student Enrollment Analysis
        </Typography>
        {/* Live counts for quick sanity check */}
        <Typography sx={{ color: C.white, opacity: 0.75, mt: 0.5, fontSize: 13 }}>
          Total loaded: {students.length} students &nbsp;|&nbsp; Showing: {filtered.length} after filters
        </Typography>
      </Box>

      {loading && (
        <LinearProgress sx={{
          mb: 3, borderRadius: 1, bgcolor: `${C.primary}20`,
          '& .MuiLinearProgress-bar': { bgcolor: C.primary },
        }} />
      )}

      {/* Filters */}
      <GlassCard sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterList sx={{ color: C.primary }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: C.primary }}>
              Filter Students
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth label="Start Date" type="date"
                value={dateRange.start}
                onChange={e => setDateRange(d => ({ ...d, start: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: C.primary, fontSize: 18 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth label="End Date" type="date"
                value={dateRange.end}
                onChange={e => setDateRange(d => ({ ...d, end: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <CalendarToday sx={{ mr: 1, color: C.primary, fontSize: 18 }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setPage(0); }} label="Class">
                  <MenuItem value="all">All Classes</MenuItem>
                  {['Pre-school','STD I','STD II','STD III','STD IV','STD V','STD VI','STD VII'].map(c => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select value={selectedGender} onChange={e => { setSelectedGender(e.target.value); setPage(0); }} label="Gender">
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, flexWrap: 'wrap' }}>
            <SchoolButton variant="outlined" startIcon={<Refresh />} onClick={resetFilters}
              sx={{ borderColor: C.primary, color: C.primary }}>
              Reset Filters
            </SchoolButton>
            <SchoolButton variant="outlined" startIcon={<Refresh />} onClick={fetchData}
              sx={{ borderColor: C.secondary, color: C.secondary }}>
              Refresh Data
            </SchoolButton>
            <SchoolButton variant="contained" startIcon={<FileDownload />} onClick={handleExportExcel}
              disabled={filtered.length === 0}
              sx={{ background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, color: C.white }}>
              Export Excel
            </SchoolButton>
            <SchoolButton variant="contained" startIcon={<PictureAsPdf />} onClick={handleExportPDF}
              disabled={filtered.length === 0}
              sx={{ background: `linear-gradient(135deg, ${C.accent}, #e65100)`, color: C.white }}>
              Export PDF
            </SchoolButton>
          </Box>
        </CardContent>
      </GlassCard>

      {/* Stat cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { color: C.primary,   icon: <School />,    value: filtered.length,              label: 'Filtered Students' },
          { color: C.accent,    icon: <ChildCare />, value: stats.byStudentType.day,       label: 'Day Scholars' },
          { color: C.purple,    icon: <ChildCare />, value: stats.byStudentType.boarding,  label: 'Boarding' },
          { color: C.teal,      icon: <Science />,   value: stats.byClass.length,          label: 'Classes' },
        ].map(({ color, icon, value, label }, i) => (
          <Grid item xs={12} sm={6} md={3} key={label}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: i * 0.1 }}>
              <StyledCard color={color}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: `${color}25`, color, width: 52, height: 52 }}>{icon}</Avatar>
                    <Typography variant="h3" sx={{ fontWeight: 700, color }}>{value}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{label}</Typography>
                  <LinearProgress variant="determinate"
                    value={students.length > 0 ? Math.min((value / students.length) * 100, 100) : 0}
                    sx={{
                      mt: 2, height: 6, borderRadius: 3, bgcolor: `${color}18`,
                      '& .MuiLinearProgress-bar': { bgcolor: color },
                    }}
                  />
                </CardContent>
              </StyledCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { id: 'gender', title: 'Gender Distribution',  icon: <Wc />,           chart: <Doughnut data={genderChart} options={chartOptions} /> },
          { id: 'type',   title: 'Student Type',         icon: <DonutLarge />,   chart: <Pie      data={typeChart}   options={chartOptions} /> },
          { id: 'class',  title: 'Students by Class',    icon: <BarChartIcon />, chart: <Bar      data={classChart}  options={chartOptions} /> },
          { id: 'trend',  title: 'Enrollment Trend',     icon: <Timeline />,     chart: <Line     data={trendChart}  options={chartOptions} /> },
        ].map(({ id, title, icon, chart }) => (
          <Grid item xs={12} md={6} key={id}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <GlassCard>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {React.cloneElement(icon, { sx: { color: C.primary } })}
                      <Typography variant="h6" sx={{ color: C.primary, fontWeight: 600 }}>{title}</Typography>
                    </Box>
                  }
                  action={
                    <IconButton onClick={() => toggleExpand(id)} sx={{ color: C.primary }}>
                      {expanded[id] === false ? <ExpandMore /> : <ExpandLess />}
                    </IconButton>
                  }
                />
                <Collapse in={expanded[id] !== false}>
                  <CardContent>
                    <Box sx={{ height: 280 }}>{chart}</Box>
                  </CardContent>
                </Collapse>
              </GlassCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Student table */}
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
        <GlassCard>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChart sx={{ color: C.primary }} />
                <Typography variant="h6" sx={{ color: C.primary, fontWeight: 600 }}>Student List</Typography>
              </Box>
            }
            action={
              <Chip
                label={`${filtered.length} of ${students.length} students`}
                size="small"
                sx={{
                  bgcolor: `${C.primary}15`, color: C.primary, fontWeight: 600,
                  border: `1px solid ${C.primary}30`,
                }}
              />
            }
          />
          <CardContent sx={{ p: 0 }}>
            {!loading && filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <School sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                {students.length === 0 ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>No students loaded</Typography>
                    <Typography variant="body2">
                      Could not fetch students from the server. Check your network or API.
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      No students match the current filters
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {students.length} student(s) loaded — try widening the date range or changing class/gender filters.
                    </Typography>
                    <Button variant="outlined" onClick={resetFilters}
                      sx={{ borderColor: C.primary, color: C.primary }}>
                      Reset Filters
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {['Reg No.','Full Name','Gender','Class','Type','Father\'s Name','Mother\'s Name','DOB'].map(col => (
                          <TableCell key={col} sx={{ bgcolor: C.primary, color: C.white, fontWeight: 700, fontSize: 13 }}>
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filtered
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map(s => (
                          <TableRow key={s.id ?? s.registration_number} hover
                            sx={{ '&:hover': { bgcolor: `${C.primary}06` } }}>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: C.primary, fontSize: 12 }}>
                              {s.registration_number}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{
                                  width: 28, height: 28, fontSize: 12, fontWeight: 700,
                                  background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
                                }}>
                                  {(s.full_name || '?').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.full_name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                icon={s.gender?.toLowerCase() === 'male' ? <Male /> : <Female />}
                                label={s.gender || '—'}
                                variant="outlined"
                                sx={{
                                  borderColor: s.gender?.toLowerCase() === 'male' ? C.secondary : C.accent,
                                  color:       s.gender?.toLowerCase() === 'male' ? C.secondary : C.accent,
                                  fontSize: 11,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={(s.current_class || '').trim() || '—'}
                                sx={{
                                  bgcolor: `${CLASS_COLORS[(s.current_class || '').trim()] || C.primary}20`,
                                  color:   CLASS_COLORS[(s.current_class || '').trim()] || C.primary,
                                  fontWeight: 600, fontSize: 11,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={s.student_type || 'Day'}
                                color={s.student_type?.toLowerCase() === 'boarding' ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: 13 }}>{s.father_name || '—'}</TableCell>
                            <TableCell sx={{ fontSize: 13 }}>{s.mother_name || '—'}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                              {parseDate(s.date_of_birth)?.toLocaleDateString() ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50]}
                  component="div"
                  count={filtered.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
              </>
            )}
          </CardContent>
        </GlassCard>
      </motion.div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}